// src/components/shared/Toast.tsx
import { useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useAlertStore } from "@/store/alertStore";

interface SystemAlert {
  id: string;
  type: "error" | "warning" | "info" | "success";
  title: string;
  message: string;
  timestamp: number;
  dismissed: boolean;
}

const TYPE_STYLES: Record<string, string> = {
  error: "bg-red-50 border-red-200 text-red-800",
  warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
  success: "bg-green-50 border-green-200 text-green-800",
};

const AUTO_DISMISS_MS = 6000;

export function ToastContainer() {
  const alerts = useAlertStore((s) => s.alerts);
  const dismissAlert = useAlertStore((s) => s.dismissAlert);
  const visible = (alerts as SystemAlert[]).filter((a) => !a.dismissed).slice(0, 5);

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
      aria-live="polite"
    >
      {visible.map((alert) => (
        <ToastItem key={alert.id} alert={alert} onDismiss={dismissAlert} />
      ))}
    </div>
  );
}

function ToastItem({
  alert,
  onDismiss,
}: {
  alert: SystemAlert;
  onDismiss: (id: string) => void;
}) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const timer = setTimeout(() => onDismissRef.current(alert.id), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [alert.id]);

  return (
    <div
      className={`pointer-events-auto flex w-80 items-start gap-2 rounded-lg border p-3 shadow-lg ${TYPE_STYLES[alert.type] ?? TYPE_STYLES.info}`}
      role="alert"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{alert.title}</p>
        {alert.message && <p className="mt-0.5 text-xs opacity-80">{alert.message}</p>}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(alert.id)}
        className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
        aria-label="Dismiss"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
